<template>
  <div class="main-page-body">
    <div class="full-page-layout site-section dark" :class="[{ loading }, `${$store.state.campaign}-promo`]">
      <div class="container">
        <div class="campaign-banner-wrapper" :class="$store.state.campaign ? 'col-12' : 'col-10 offset-1' ">
          <div class="text-center">
            <h1 :class="$store.state.layout === 'mobile' ? 'h1' : 'h2'">
              {{ $c.text('pages.pricing.title') }}
            </h1>
            <BannerCard
              v-if="showBannerCard"
              :key="bannerCardKey(promotion).getTime()"
              :banner="$store.state.banners.active || {}"
              :custom-campaign="customCampaign"
              pricing
            />
            <p v-else class="subtitle">
              {{ $c.text('pages.pricing.subtitle') }}
            </p>
          </div>
        </div>
        <div v-if="promoBanner.type === 2 && !$store.state.campaign" class="counter">
          <div class="square days">
            <span>{{ countdown.days }}</span><span>Days</span>
          </div>
          <div class="square hours">
            <span>{{ countdown.hours }}</span><span>Hours</span>
          </div>
          <div class="square min">
            <span>{{ countdown.minutes }}</span><span>Minutes</span>
          </div>
          <div class="square sec">
            <span>{{ countdown.seconds }}</span><span>Seconds</span>
          </div>
        </div>
        <div class="plan-cards" :class="[{ 'card-number': version === '1', 'free-campaign': $config.campaign === 'free-days-2024', 'jumpstart-cards': $config.campaign === 'jumpstart' }, $store.state.campaign ? `${$store.state.campaign}-cards` : '']">
          <div class="row">
            <div class="col-3 col free card-wrapper">
              <PlanCard
                :version="version"
                :plan="{
                  shortTitle: 'Free',
                  price: 0,
                  free: true
                }"
                :stats="stats"
                :campaign="isPlanCardCampaign"
                @subscribe="$router.push('/signup')"
              />
            </div>
            <div class="col-3 col monthly card-wrapper">
              <PlanCard
                :version="version"
                :plan="$store.getters['subscriptions/monthly']"
                :stats="stats"
              />
            </div>
            <div class="col-3 col annual card-wrapper">
              <PlanCard
                :discount="promotion.discount || 0"
                :version="version"
                :plan="$store.getters['subscriptions/annual']"
                :stats="stats"
                :campaign="isPlanCardCampaign"
                :additional-class="planCardAdditionalClass"
              />
            </div>
            <div v-if="version === '1'" class="col-3 col lifetime card-wrapper">
              <PlanCard
                :version="version"
                :plan="$store.getters['subscriptions/lifetime']"
                :stats="stats"
              />
            </div>
            <div v-if="version === '0' && $config.platform !== 'fa'" class="col-3 col business card-wrapper">
              <PlanCard
                class="business"
                :plan="$store.getters['subscriptions/business']"
                :stats="stats"
              />
            </div>
          </div>
        </div>
        <div v-if="$store.state.campaign !== 'jumpstart'" class="col-12 text-center">
          <div class="body-1">
            <Badges class="badges pricing" />
            <p>30-day money-back guarantee</p>
            <p>VAT may apply</p>
          </div>
        </div>
      </div>
    </div>
    <Lazy never>
      <GraduatesWork
        class="site-section tracking-section"
        face="secondary"
        :home="false"
        data-event="ourgraduates.scroll"
      />
    </Lazy>
    <Lazy :when-visible="{ rootMargin: '200px' }">
      <FrequentlyAskedQuestions
        group="pricing" class="site-section tracking-section" :results="pageData.faqResults"
        data-event="faq.scroll"
      />
    </Lazy>
    <Lazy never>
      <Reviews dark class="tracking-section" data-event="reviews.scroll" />
    </Lazy>
  </div>
</template>

<script>
import Lazy from 'vue-lazy-hydration';
import moment from 'moment';
import PlanCard from '@itbpbg/lms-components/src/Pricing/PlanCardNew';
import BannerCard from '@itbpbg/lms-components/src/Banners/BannerCard';
import FrequentlyAskedQuestions from '@itbpbg/lms-components/src/FrequentlyAskedQuestions';
import Reviews from '@itbpbg/lms-components/src/Reviews';
import {
  countDownToDate, calculateSubscriptionsLeft, getPromotion, dateInRightInterval,
} from '@itbpbg/lms-components/src/utils/helper';
import { CAMPAIGN_PROMOTIONS } from '@itbpbg/lms-components/src/utils/campaigns';
import Badges from '@itbpbg/lms-components/src/Pricing/Badges';
import campaigns from '@itbpbg/lms-components/src/mixins/campaigns';
import GraduatesWork from '../GraduatesWork';

export default {
  components: {
    Lazy,
    PlanCard,
    BannerCard,
    FrequentlyAskedQuestions,
    Badges,
    GraduatesWork,
    Reviews,
  },
  mixins: [campaigns],
  props: {
    pageData: { type: Object, default: () => ({}), required: true },
  },
  data: () => ({
    countdown: {
      days: '00',
      hours: '00',
      minutes: '00',
      seconds: '00',
    },
    loading: false,
    countdownInterval: null,
    version: '0',
    discount: 0,
    currentEndDate: '',
    promotion: {},
    currentDate: new Date(),
  }),
  computed: {
    campaignWithoutBanner() {
      return this.$store.state.campaign === 'cyber-monday-lifetime-2024';
    },
    isPlanCardCampaign() {
      if (this.campaignWithoutBanner) {
        return '';
      }

      return new Date(this.promotion.lastEndDate) > new Date() ? this.$store.state.campaign : '';
    },
    showBannerCard() {
      if (this.campaignWithoutBanner) {
        return false;
      }

      return this.$store.state.campaign && this.$store.state.banners.active && new Date(this.promotion.lastEndDate) > new Date();
    },
    promoBanner() {
      const banner = this.$store.getters['banners/active'] || {};
      if (banner.type === 2 && !this.$store.state.campaign) {
        return banner;
      }
      return {};
    },
    subscriptionsLeft() {
      if (this.$store.state.campaign === 'one-of-two-hundred-2024') {
        return calculateSubscriptionsLeft(this.currentDate, this.promotion);
      }

      return 0;
    },
    customCampaign() {
      if (this.$store.state.campaign) {
        const campaignData = CAMPAIGN_PROMOTIONS[this.$store.state.campaign];
        let promotionData = null;
        if (typeof campaignData === 'function') {
          promotionData = campaignData();
        } else if (typeof campaignData === 'object') {
          promotionData = campaignData;
        }

        return { ...promotionData.templates, ...this.promotion, subscriptionsLeft: this.subscriptionsLeft };
      }

      return {};
    },
    page() {
      return this.pageData.page || {};
    },
    stats() {
      if (this.pageData.stats) {
        return this.pageData.stats;
      }

      return { rounded: {}, raw: {} };
    },
    planCardAdditionalClass() {
      const classes = {};

      if (this.promotion.extend) {
        classes.extend = this.promotion.extend;
      }

      return classes;
    },
  },
  watch: {
    promoBanner(v) {
      if (v.active) {
        this.startCountdown();
      }
    },
  },
  created() {
    if (this.promoBanner.active || this.$store.state.campaign) {
      this.startCountdown();
    }
  },
  methods: {
    startCountdown() {
      if (this.countdownInterval) {
        return;
      }
      const date = this.promoBanner.endDate;

      if (this.$store.state.campaign) {
        this.getCurrentPromotion();
      }

      const utcDate = moment.utc(`${date}`);
      // const secondEndDateUtc = moment.utc('April 01, 2023 06:00:00');
      // if (utcDate < moment().utc()) {
      //   utcDate = secondEndDateUtc;
      // }
      const localTime = utcDate.local();
      const countDownDate = new Date(localTime);
      const nowDate = new Date();
      this.countdown = countDownToDate(new Date(), countDownDate);
      this.countdownInterval = setInterval(() => {
        if (this.$store.state.campaign === 'one-of-two-hundred-2024') {
          if (dateInRightInterval(new Date())) {
            this.currentDate = new Date();
            this.getCurrentPromotion();
          }
        }

        if (this.$config.campaign === 'top-finance-training') {
          this.currentDate = new Date();
          this.getCurrentPromotion();
        }
        this.countdown = countDownToDate(nowDate, countDownDate);
      }, 1000);
      this.$once('hook:beforeDestroy', () => clearInterval(this.countdownInterval));
    },
    getCurrentPromotion() {
      const campaignData = CAMPAIGN_PROMOTIONS[this.$store.state.campaign];
      let promotionData = null;
      if (typeof campaignData === 'function') {
        promotionData = campaignData();
      } else if (typeof campaignData === 'object') {
        promotionData = campaignData;
      }

      if (promotionData) {
        const promoOptions = getPromotion(promotionData.promos, this.promotion);

        this.promotion = {
          ...promoOptions.nextPromotion,
          lastEndDate: promoOptions.lastEndDate,
        };

        if (promoOptions.refresh && process.client) {
          this.$store.dispatch('subscriptions/fetchPrices');
          this.$store.dispatch('banners/fetch');
          window.$nuxt.refresh();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.full-page-layout {
  position: relative;
  margin-bottom: 20px;

  &:before {
    background: linear-gradient(253.23deg, rgba(0, 164, 178, 0.2) 0%, rgba(0, 164, 178, 0.05) 44.75%, rgba(0, 164, 178, 0) 94.77%);
    transform: matrix(-1, 0, 0, 1, 0, 0);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100%;
    width: 100%;
  }

  .container {
    padding-top: var(--spacing-100);

    .subtitle {
      margin: 0 auto;
    }

    .campaign-banner {
      background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), #141F31;
      border-radius: 10px;
      padding: var(--spacing-32) 10px;

      h4 {
        font-weight: 700;
        color: var(--main-yellow);
        margin-bottom: var(--spacing-8);
      }

      p {
        width: 85%;
        margin: 0 auto;
        line-height: 150%;
      }

      .campaign-counter {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: var(--spacing-16);

        .square {
          display: flex;
          flex-direction: column;
          width: 59px;

          span {
            font-size: 28px;
            line-height: 110%;
            color: var(--main-light-shade-2);
            font-weight: 500;

            &:last-of-type {
              font-weight: 400;
              font-size: 14px;
              line-height: 130%;
              color: var(--main-light-shade-3);
            }
          }
        }

        .colon {
          font-size: 32px;
          line-height: 100%;
          margin: 0 var(--spacing-12);
          color: var(--main-light-shade-2);
        }
      }

      &.black-friday {
        background-image: url("@itbpbg/lms-components/src/assets/images/pricing-black-friday-campaign-banner.png");
        border: 5px solid rgba(25, 95, 245, 0.5);
        border-radius: 0;
        padding: 27px 10px;
        margin-top: var(--spacing-40);

        ::v-deep .holder-groups {
          .groups {
            height: 100%;
            width: 18%;
            position: relative;
            margin-right: 8px;
          }
        }

        .title-small {
          text-transform: capitalize;
          color: var(--extreme-blue-2);
          margin-bottom: var(--spacing-8);
        }

        p {
          margin-top: var(--spacing-12);
        }
      }

      &.finance-courses-offer {
        width: 850px;
        margin: 0 auto;
        background-image: url("@itbpbg/lms-components/src/assets/images/pricing-cyber-monday-banner.jpg");
        background-position: center;
        background-repeat: no-repeat;
        margin-top: var(--spacing-40);
        padding: 38px var(--spacing-90) var(--spacing-32);
        border-radius: 0;
        position: relative;

        &:after {
          content: "";
          position: absolute;
          width: calc(100% - 10px);
          height: calc(100% - 10px);
          top: 0;
          left: 0;
          border-image: linear-gradient(to bottom, rgba(25, 95, 245, 0.5), rgba(25, 245, 238, 0.5)) 30;
          border-width: 5px;
          border-style: solid;
        }

        .title-small {
          font-size: 24px;
          line-height: 110%;
          text-transform: capitalize;
          color: #19F5EE;
          margin-bottom: var(--spacing-8);
        }

        .h1 {
          font-weight: 900;
          font-size: 42px;
          line-height: 50px;
          text-transform: uppercase;
          text-shadow: 0 3.10633px 7.54395px rgba(37, 244, 238, 0.5), 2.21881px 7.10018px 12.4253px #2D0CEC;
          margin-bottom: var(--spacing-12);
        }

        p {
          width: 100%;
        }

        .campaign-counter {
          .square {
            span:first-of-type {
              color: #F0F0F2;
            }
          }
        }
      }

      &.jumpstart {
        background: none;
        background-image: url("@itbpbg/lms-components/src/assets/images/banners/jumpstart-2023-pricing-banner.jpg");
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        position: relative;
        margin-top: var(--spacing-40);

        &:after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: calc(100% - 8px);
          height: calc(100% - 8px);
          border: 4px solid rgba(111, 197, 201, 0.4);
          border-radius: 10px;
        }

        h1 {
          font-weight: 900;
          font-size: 45px;
          line-height: 53px;
          text-shadow: 0 3.47052px 8.42841px rgba(37, 244, 238, 0.5), 0 0 13.8821px rgba(25, 245, 238, 0.5);
          margin-bottom: var(--spacing-8);
        }

        p {
          max-width: 670px;
          margin-bottom: var(--spacing-20);

          .highlighted {
            font-weight: 700;
          }
        }

        ::v-deep .counter-with-discount {
          width: 426px;
          margin: 0 auto;
          border: 1px solid var(--extreme-blue-2);
          border-radius: 10px;

          .square {
            width: 80px;
            border: none;
            padding: var(--spacing-18) var(--spacing-12);
            margin-right: auto;

            span {
              line-height: 39px;
              color: var(--main-light-shade-2);
            }

            p {
              line-height: 18px;
              color: var(--main-light-shade-2);
              width: 50px;
            }

            &.discount {
              width: 142px;
              border-radius: 0;
              border-right: 1px solid var(--extreme-blue-2);

              span {
                font-weight: 700;
                color: var(--extreme-blue-2);
                text-shadow: 0 3.47052px 8.42841px rgba(37, 244, 238, 0.5), 0 0 13.8821px rgba(25, 245, 238, 0.5);
              }

              p {
                font-weight: 600;
                color: var(--main-light);
                letter-spacing: 0.02em;
                width: 118px;
              }
            }
          }
        }
      }

      &.free-days-2023 {
        max-width: 858px;
        margin: 0 auto;
        background: linear-gradient(179.58deg, rgba(3, 111, 253, 0.14) -0.05%, rgba(3, 111, 253, 0.05) 25.66%, rgba(1, 22, 51, 0) 99.77%), #283454;
        padding: var(--spacing-32) var(--spacing-60);

        p {
          width: 97%;
        }

        .campaign-counter {
          .colon {
            padding-bottom: var(--spacing-16);
          }
        }
      }

      &.got-what-it-takes {
        background: none;
        background-image: url("@itbpbg/lms-components/src/assets/images/banners/gwt-pricing-banner-bg.jpg");
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        padding: var(--spacing-44) 235px 54px;
        margin-top: var(--spacing-40);

        .heading {
          margin-bottom: var(--spacing-16);

          .h6 {
            font-size: 16px;
            line-height: 150%;
            margin-bottom: var(--spacing-8);
          }

          .campaign-title {
            position: relative;
            display: flex;
            justify-content: space-between;
            margin: 0;
            width: 100%;
            height: 49px;
            top: -2px;
            font-weight: 700;
            font-size: 46px;
            line-height: 110%;

            .highlighted-left,
            .highlighted-right {
              text-transform: uppercase;
              color: var(--main-light);
              display: inline-block;
            }

            .highlighted-left {
              padding-left: var(--spacing-8);
              text-shadow:
                0 0 0 #56BFC7,
                -1px 1px 0 #56BFC7,
                -2px 2px 0 #56BFC7,
                -3px 3px 0 #56BFC7,
                -4px 4px 0 #56BFC7,
                -5px 5px 0 #56BFC7,
                -6px 6px 0 #56BFC7,
                -7px 7px 0 #56BFC7,
                -8px 8px 0 #56BFC7,
                -9px 9px 0 #56BFC7,
                -10px 10px 0 #56BFC7;
            }

            .highlighted-right {
              text-shadow:
                0 0 0 #CEA40A,
                -1px 1px 0 #CEA40A,
                -2px 2px 0 #CEA40A,
                -3px 3px 0 #CEA40A,
                -4px 4px 0 #CEA40A,
                -5px 5px 0 #CEA40A,
                -6px 6px 0 #CEA40A,
                -7px 7px 0 #CEA40A,
                -8px 8px 0 #CEA40A,
                -9px 9px 0 #CEA40A,
                -10px 10px 0 #CEA40A;
            }
          }
        }

        .campaign-description {
          font-weight: 500;
          max-width: 670px;
          width: 100%;
          margin-bottom: var(--spacing-20);
        }

        .campaign-counter {
          .square {
            width: 56px;
          }

          .colon {
            margin: 0 var(--spacing-8) var(--spacing-12);
          }
        }
      }

      &.one-of-two-hundred {
        background-image: url("@itbpbg/lms-components/src/assets/images/banners/oth-pricing-banner-bg.jpg");
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        position: relative;
        padding: var(--spacing-44) 170px;

        &:after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: calc(100% - 20px);
          height: calc(100% - 20px);
          border: 10px solid rgba(255, 255, 255, 0.3);
          border-radius: 10px;
        }

        .campaign-title {
          letter-spacing: normal;
          margin-bottom: var(--spacing-8);

          .line-1 {
            font-weight: 700;
            font-size: 28px;
            line-height: 110%;
            margin-bottom: var(--spacing-12);

            .highlighted {
              display: inline-block;
              color: var(--extreme-yellow);
              font-weight: 800;
            }
          }

          .line-2 {
            font-weight: 500;
            font-size: 24px;
            line-height: 110%;

            .subscriptions {
              width: 56px;
              height: 41px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              background: var(--main-dark-light-2);
              border: 1px solid var(--main-dark-light-4);
              border-radius: 10px;
              font-weight: 700;
              color: var(--extreme-fucsia);
              margin: 0 var(--spacing-12);
            }
          }
        }
      }
    }

    .row {
      justify-content: center;
    }

    .col-3 {
      .campaign-overlay {
        position: absolute;
        top: -35px;
        left: 8px;
        right: 8px;
        bottom: -5px;
        border-radius: 10px;
        z-index: 0;

        span {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 35px;
          color: var(--main-dark-shade-0);
          font-weight: 600;
          font-size: 18px;
        }
      }

      .campaign-overlay + .card {
        z-index: 1;
        position: relative;
      }
    }
  }

  .h6 {
    -webkit-transition: 0.4s;
    -ms-transition: 0.4s;
    -moz-transition: 0.4s;
    -o-transition: 0.4s;
    transition: 0.4s;
  }

  .unactive,
  .subtitle,
  .body-1 {
    color: var(--main-light-shade-4);
  }

  .body-1 {
    p {
      margin-bottom: 0;
    }
  }

  .counter {
    margin-top: 44px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    .square {
      width: 80px;
      height: 88px;
      box-sizing: border-box;
      border-radius: 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-direction: column;
      margin: 0 var(--spacing-8);
      padding: 12px 24px;
      border: 1px solid var(--main-dark-shade-3);

      span {
        font-size: 40px;
        line-height: 110%;
        color: var(--main-light-shade-3);

        &:last-of-type {
          color: var(--main-light-shade-3);
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
        }
      }
    }
  }

  .plan-cards {
    margin-top: var(--spacing-40);
    margin-bottom: var(--spacing-32);

    .card-holder {
      width: 100%;
      max-width: 280px;

      &:nth-of-type(2) {
        margin-right: var(--spacing-18);
        margin-left: var(--spacing-18);
      }
    }

    &.card-number {
      .row {
        .card {
          width: 270px;
        }

        .col {
          padding-left: 10px !important;
          padding-right: 10px !important;
        }
      }
    }

    &.jumpstart-cards {
      .row {
        column-gap: 20px;
        margin: 0;
        padding: 0 15px;

        .col {
          max-width: 270px;
          margin: 0;
          padding: 0;

          ::v-deep .card {
            .campaign-btn {
              height: 58px;
            }
          }
        }
      }
    }

    &.free-campaign {
      .annual {
        ::v-deep .card {
          .sale,
          .best-price {
            display: none;
          }

          .btn-free-days-2023 {
            background-color: transparent;
            color: var(--accent);
            border-width: 2px;

            &:hover {
              border-color: var(--accent-shade-1);
              background-color: var(--accent-shade-1);
              color: var(--main-light);
            }

            &:active {
              background-color: var(--accent-shade-2);
              border-color: var(--accent-shade-2);
              color: var(--main-light);
            }
          }
        }
      }
    }

    &.got-what-it-takes-cards {
      .row {
        margin: 0;
        padding: 0 15px;
        column-gap: 20px;

        .col {
          padding: 0;
          max-width: 270px;
        }
      }
    }

    &.one-of-two-hundred-2024-cards {
      margin-top: 54px;
    }

    &.top-finance-training-cards {
      .card-wrapper {
        padding-left: 15px !important;
        padding-right: 15px !important;
      }
    }
  }

  > div {
    transition: opacity 100ms ease-in;
  }

  &.loading > div {
    visibility: hidden;
    opacity: 0;
  }
}

@include bp($bp-laptop) {
  .full-page-layout {
    .container {
      .campaign-banner {
        &.one-of-two-hundred-2024 {
          .campaign-title {
            .line-1 {
              font-size: 24px;
              margin-bottom: var(--spacing-8);
            }

            .line-2 {
              font-size: 21px;

              .subscriptions {
                width: 51px;
                height: 39px;
                font-size: 21px;
              }
            }
          }

          .campaign-description p {
            width: 100%;
          }
        }
      }
    }

    .plan-cards {
      &.one-of-two-hundred-2024-cards {
        margin-top: var(--spacing-40);
      }
    }
  }

  ::v-deep {
    .reviews {
      .h2 {
        font-size: 40px;
      }
    }
  }
}

@include bp($bp-mobile) {
  .plan-cards {
    .row {
      margin: 0 -12px;

      .free {
        order: 3;
      }

      .monthly {
        order: 2;
      }

      .annual {
        order: 1;
      }

      .business {
        order: 4;
      }
    }

    &.card-number {
      .row {
        .free {
          order: 3;
        }

        .monthly {
          order: 2;
        }

        .annual {
          order: 1;
        }

        .lifetime {
          order: 4;
        }

        .card {
          width: unset !important;
        }
      }
    }

    &.free-campaign,
    &.free-weeks-2024-cards {
      .free {
        order: 1;
      }

      .monthly {
        order: 2;
      }

      .annual {
        order: 3;
      }

      .business {
        order: 4;
      }
    }
  }

  .full-page-layout {
    margin-bottom: 0;

    &.site-section .plan-cards {
      margin-top: var(--spacing-24);
    }

    .container {
      padding-top: var(--spacing-60);

      .campaign-banner-wrapper {
        .campaign-banner {
          margin-top: var(--spacing-40);
          padding: var(--spacing-32);

          h4 {
            font-size: 24px;
          }

          .campaign-counter {
            .colon {
              margin: 0 var(--spacing-8);
            }
          }

          &.black-friday {
            .title-small {
              font-size: 24px;
            }

            ::v-deep .holder-groups {
              .groups {
                height: 30px;
                margin: 0;
                width: 100%;

                .group-1,
                .group-2 {
                  font-size: 30px;
                }
              }
            }
          }

          &.finance-courses-offer {
            width: 100%;
            background-size: cover;
          }

          &.free-days-2023 {
            max-width: 100%;
          }

          &.got-what-it-takes {
            .heading {
              .campaign-title {
                max-width: 670px;
                margin-left: auto;
                margin-right: auto;
              }
            }

            .campaign-counter {
              margin-top: 0;

              .colon {
                margin: 0 var(--spacing-8) var(--spacing-12);
              }
            }

            @media screen and (max-width: 790px) {
              padding-top: var(--spacing-44);
              padding-bottom: var(--spacing-44);

              .heading {
                margin-bottom: var(--spacing-8);

                .campaign-title {
                  flex-direction: column;
                  height: 94px;

                  .highlighted-left {
                    padding: 0;
                    height: 52px;
                    font-size: 47px;
                  }

                  .highlighted-right {
                    height: 35px;
                    font-size: 32px;
                    position: relative;
                    top: -10px;
                    text-shadow:
                      0 0 0 #CEA40A,
                      -1px 1px 0 #CEA40A,
                      -2px 2px 0 #CEA40A,
                      -3px 3px 0 #CEA40A,
                      -4px 4px 0 #CEA40A,
                      -5px 5px 0 #CEA40A,
                      -6px 6px 0 #CEA40A,
                      -7px 7px 0 #CEA40A,
                      -8px 8px 0 #CEA40A;
                  }
                }
              }

              .campaign-description {
                margin-bottom: var(--spacing-24);
              }
            }
          }

          &.one-of-two-hundred-2024 {
            padding: var(--spacing-40) var(--spacing-20);

            .campaign-title {
              margin-bottom: var(--spacing-16);

              .line-1 {
                margin-bottom: var(--spacing-16);

                span:last-of-type {
                  display: block;
                }
              }
            }

            .campaign-description p {
              width: 100%;
            }
          }
        }
      }

      .plan-cards {
        &.jumpstart-cards {
          .row {
            padding: 0;

            ::v-deep .col {
              max-width: 100%;
              margin-bottom: var(--spacing-18);

              h2 {
                margin-bottom: var(--spacing-4);
              }

              &.annual {
                margin-bottom: var(--spacing-28);
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

                .sale {
                  > span {
                    width: 146px;
                  }
                }

                h2 {
                  margin-bottom: var(--spacing-4);
                  margin-top: 34px;

                  .current-price {
                    .body-1 {
                      font-weight: 400;
                    }
                  }
                }

                .caption {
                  .save-label {
                    padding: 5px 7px;
                    font-weight: 500;
                  }
                }
              }

              &.business {
                h2 {
                  .current-price {
                    justify-content: flex-start;

                    .body-1 {
                      line-height: 150%;
                      width: fit-content;
                    }
                  }
                }
              }
            }
          }
        }

        &.got-what-it-takes-cards {
          .row {
            padding: 0;

            .col {
              margin-top: var(--spacing-28);
              max-width: 100%;

              &.annual {
                margin-top: 0;
              }
            }
          }
        }

        &.one-of-two-hundred-2024-cards {
          .card-wrapper {
            &.free,
            &.monthly,
            &.annual,
            &.business {
              padding: 0 var(--spacing-12);
            }
          }
        }
      }

      .free-campaign {
        margin-top: 0;

        .row {
          margin-top: var(--spacing-44);
        }
      }

      .col-3 {
        margin-top: var(--spacing-18);
      }
    }
  }
}

@include bp($bp-phone) {
  .full-page-layout {
    .container {
      .campaign-banner-wrapper {
        .finance-courses-offer {
          background-image: url("@itbpbg/lms-components/src/assets/images/pricing-cyber-monday-banner-mobile.png");
          padding: var(--spacing-40) 17px var(--spacing-36);

          .title-small {
            font-size: 18px;
          }

          .h1 {
            font-size: 35px;
            line-height: 41px;
            text-shadow: 0 2.01935px 4.90414px rgba(37, 244, 238, 0.5), 1.44239px 4.61566px 8.07741px #2D0CEC;
          }

          p {
            max-width: 263px;
            margin: 0 auto;
          }
        }

        .jumpstart {
          background-image: url("@itbpbg/lms-components/src/assets/images/banners/jumpstart-2023-pricing-banner-mobile.jpg");
          padding-top: var(--spacing-40);

          h1 {
            font-size: 30px;
            line-height: 38px;
            text-shadow: 0 2.50755px 6.08976px rgba(37, 244, 238, 0.5), 0 0 10.0302px rgba(25, 245, 238, 0.5);
            margin-bottom: var(--spacing-12);
          }

          p {
            margin: 0;
            width: 100%;
          }

          ::v-deep .counter-with-discount {
            margin-top: var(--spacing-16);
            height: 76px;
            width: 100%;
            justify-content: flex-start;

            .square {
              margin-right: auto;
              padding: var(--spacing-16) 0;
              height: 100%;
              width: 40px;

              span {
                font-weight: 500;
                font-size: 26px;
                line-height: 110%;
              }

              p {
                font-size: 10px;
              }

              &.discount {
                width: 100px;
                padding-left: 10px;
                padding-right: 10px;

                span {
                  text-shadow: 0 2.79128px 6.77881px rgba(37, 244, 238, 0.5), 0 0 11.1651px rgba(25, 245, 238, 0.5);
                }

                p {
                  width: 100px;
                }
              }
            }
          }

          @media screen and (max-width: 360px) {
            height: auto;

            ::v-deep .counter-with-discount {
              height: fit-content;
              flex-wrap: wrap;

              .square {
                margin: 0 auto;

                &.discount {
                  flex-basis: 100%;
                  border-right: none !important;
                  border-bottom: 1px solid var(--extreme-blue-2);
                  margin-right: 0 !important;

                  p {
                    width: 100% !important;
                  }
                }
              }
            }
          }
        }

        .got-what-it-takes {
          background-image: url("@itbpbg/lms-components/src/assets/images/banners/gwt-pricing-banner-mobile-bg.jpg");
          padding: var(--spacing-44) 17px;
        }

        .one-of-two-hundred-2024 {
          background-image: url("@itbpbg/lms-components/src/assets/images/banners/oth-pricing-banner-mobile-bg.jpg");

          .campaign-title {
            .line-1 {
              font-size: 28px;

              span:nth-child(2) {
                display: block;
              }
            }

            .line-2 {
              font-size: 24px;

              .new-line {
                margin-top: var(--spacing-8);
                display: block;
              }
            }
          }
        }
      }

      .plan-cards {
        .row {
          margin-top: 0;
        }
      }
    }
  }
}

.fa {
  @import '../../assets/sass/fa/pages/pricing.scss';
}
</style>
